import { useCenterStore } from '@caradasstore/CenterStore'
import { BillingSettings } from '@components/billing'
import { NetworkParticipationCard } from '@components/network/NetworkParticipationCard'
import { EditCalibrationForm } from '@components/settings'
import { TimeZoneSettings } from '@components/settings/time-zone'
import { GeneralSettings } from '@components/settings/general'
import { UserQueue } from '@components/user'
import { AdasBox, AdasTypography } from '@components/wrapper-components'
import { CccConnectionCard } from '@components/settings/ccc-connection'
import { Building2, CreditCard, Link2, Puzzle, Search, Users, Waypoints } from 'lucide-react'
import { useModuleAccess } from '@hooks/useModuleAccess'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ccidFormatter } from '../../utils/utils'

// Center → Profile: the center profile plus the Regional time-zone control below it.
// The control derives editable (Admin/Manager/Owner) vs read-only (everyone else) from the user itself.
const ProfileSettings = () => {
  const ccId = useCenterStore((store) => store.ccId)
  return (
    <>
      <EditCalibrationForm />
      <TimeZoneSettings ccId={ccId} />
    </>
  )
}

// Left-pane navigation config. Each item drives the right-pane view.
// `content` renders real content today; items without it show a placeholder.
const NAV_SECTIONS = [
  {
    title: 'Location',
    items: [
      {
        value: 'profile',
        label: 'Account',
        icon: Building2,
        content: <ProfileSettings />,
        badges: [
          { label: 'Report Tier', tone: 'muted' },
          { label: 'Manager', tone: 'accent' }
        ]
      },
      { value: 'team', label: 'Team & roles', icon: Users, content: <UserQueue /> }
    ]
  },
  {
    title: 'Billing',
    items: [
      {
        value: 'billing',
        label: 'Billing',
        title: 'Billing & Subscription',
        description:
          'Your plan, payment method, and invoices. Plans are month-to-month — subscribe, upgrade, or cancel any time; downgrades are a quick support request.',
        icon: CreditCard,
        // CAR-3710: state-driven from GET /v1/billing/subscription; Network (QBO) centers see it read-only.
        content: <BillingSettings />
      }
    ]
  },
  // The former standalone Scheduler settings tab is gone — scheduler participation
  // now lives in Platform Features (CAR-3748).
  {
    title: 'Integrations',
    items: [
      {
        value: 'ccc',
        label: 'CCC connection',
        title: 'CCC connection',
        description: 'Connect your shop to CCC ONE Secure Share so its estimates arrive here as AI Reports.',
        icon: Link2,
        // Every plan: AI Brief has no Clients page, so this is where its shop sets its CCC ID.
        content: <CccConnectionCard />
      },
      {
        value: 'quickbooks',
        label: 'QuickBooks Online',
        title: 'QuickBooks Online',
        description: 'Sync invoices and payments to your accounting system. Included with AI Complete.',
        icon: Puzzle,
        // Syncs invoices, so it goes with the Invoicing module (CAR-3710): hidden on AI Report.
        module: 'invoicing',
        content: <GeneralSettings />
      }
    ]
  },
  {
    title: 'Platform',
    items: [
      {
        value: 'platform-features',
        label: 'Platform Features',
        title: 'Platform Features',
        description: 'The platform features and referral partner networks this location is enrolled in.',
        icon: Waypoints,
        // The card derives read-only vs editable from admin status itself, so shops
        // see it read-only (with the support banner) while admins get an Edit button.
        content: <NetworkParticipationCard />
      }
    ]
  }
  // Calibration Rules & Mappings moved to the sidebar's Admin Tools group as
  // standalone admin-only pages (/calibration-rules, /mappings) — see CAR-3778.
  // ADAS Intelligence tab holds only the CCC Secure Share estimate-source
  // connection — no report preferences (CAR-3748).
]

const PlaceholderPanel = ({ title }) => (
  <AdasBox className='rounded-xl border border-border bg-white p-10 text-center'>
    <AdasTypography className='text-[16px] font-semibold text-foreground mb-2'>{title}</AdasTypography>
    <AdasTypography className='text-[14px] text-muted-foreground'>Coming soon.</AdasTypography>
  </AdasBox>
)

// Small pill badge. `tone` maps to a color treatment.
const Pill = ({ label, tone = 'muted' }) => {
  const tones = {
    muted: 'bg-slate-100 text-slate-600',
    accent: 'bg-secondary/10 text-secondary',
    violet: 'bg-violet-100 text-violet-700'
  }
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tones[tone]}`}>
      {label}
    </span>
  )
}

const ManagingCard = () => {
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const ccId = useCenterStore((store) => store.ccId)
  return (
    <AdasBox className='rounded-xl border border-border bg-muted/40 p-3 mb-4'>
      <AdasTypography className='text-[11px] font-semibold tracking-wide text-muted-foreground uppercase mb-1'>
        You are managing
      </AdasTypography>
      <AdasTypography className='text-[15px] font-bold text-foreground leading-tight'>
        {centerDetail?.name || 'Calibration Center'}
      </AdasTypography>
      {ccId ? <AdasTypography className='text-[12px] text-muted-foreground mt-0.5 mb-2'>{ccidFormatter(ccId)}</AdasTypography> : null}
    </AdasBox>
  )
}

// ?section=<item value> opens Settings on that item, so other screens can deep-link into it
// (e.g. ?section=team opens Team & roles). Anything
// unknown falls back to the first item.
const initialSection = (search) => {
  const requested = new URLSearchParams(search || '').get('section')
  const known = NAV_SECTIONS.some((section) => section.items.some((item) => item.value === requested))
  return known ? requested : 'profile'
}

export const SettingsPage = () => {
  const location = useLocation()
  const [activeValue, setActiveValue] = useState(() => initialSection(location?.search))
  const [query, setQuery] = useState('')
  const canUse = useModuleAccess()

  // Package gating (CAR-3710): an item tied to a module the center's plan lacks is dropped.
  const sections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.module || canUse(item.module))
      })).filter((section) => section.items.length > 0),
    [canUse]
  )

  const filteredSections = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sections
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.label.toLowerCase().includes(q))
      }))
      .filter((section) => section.items.length > 0)
  }, [query, sections])

  const { activeItem, activeSectionTitle } = useMemo(() => {
    for (const section of sections) {
      const item = section.items.find((i) => i.value === activeValue)
      if (item) return { activeItem: item, activeSectionTitle: section.title }
    }
    return { activeItem: null, activeSectionTitle: '' }
  }, [activeValue, sections])

  return (
    // Full-bleed shell: negate the page wrapper's horizontal padding so the white
    // panels attach flush to the edges. Left nav + top header form one white surface.
    <AdasBox className='-mx-3 min-[435px]:-mx-6 flex flex-col md:flex-row items-stretch min-h-[calc(100dvh-46px)]'>
      {/* Left pane — white, squared, attached to the left edge, full height */}
      <AdasBox className='w-full md:w-[260px] shrink-0 bg-white border-b md:border-b-0 md:border-r border-border p-4'>
        <ManagingCard />

        <AdasBox className='relative mb-4'>
          <Search className='w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2' />
          <input
            type='text'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search settings...'
            className='w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/40'
          />
        </AdasBox>

        <nav className='flex flex-col gap-4'>
          {filteredSections.map((section) => (
            <AdasBox key={section.title}>
              <AdasTypography className='text-[11px] font-semibold tracking-wide text-muted-foreground uppercase mb-2 px-1'>
                {section.title}
              </AdasTypography>
              <AdasBox className='flex flex-col gap-0.5'>
                {section.items.map((item) => {
                  const Icon = item.icon
                  const isActive = item.value === activeValue
                  return (
                    <button
                      key={item.value}
                      onClick={() => setActiveValue(item.value)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex items-center gap-3 w-full rounded-lg px-3 py-2 text-[14px] text-left transition-colors ${
                        isActive ? 'bg-secondary/10 text-secondary font-semibold' : 'text-foreground hover:bg-muted/60'
                      }`}
                    >
                      <Icon className='w-4 h-4 shrink-0' strokeWidth={1.75} />
                      <span className='truncate'>{item.label}</span>
                    </button>
                  )
                })}
              </AdasBox>
            </AdasBox>
          ))}
          {filteredSections.length === 0 && (
            <AdasTypography className='text-[13px] text-muted-foreground px-1'>No settings match “{query}”.</AdasTypography>
          )}
        </nav>
      </AdasBox>

      {/* Right column — white header strip attached on top, gray content below */}
      <AdasBox className='flex-1 min-w-0 flex flex-col'>
        {/* Header pane — white, squared, attached across the top */}
        <AdasBox className='bg-white border-b border-border px-6 py-4'>
          <AdasTypography className='text-[11px] font-semibold tracking-wide text-muted-foreground uppercase'>
            {activeSectionTitle}
          </AdasTypography>
          <AdasTypography className='text-[32px] font-bold text-foreground leading-tight'>
            {activeItem?.title ?? activeItem?.label}
          </AdasTypography>
          {activeItem?.description && (
            <AdasTypography className='text-[14px] text-muted-foreground mt-1'>{activeItem.description}</AdasTypography>
          )}
        </AdasBox>

        {/* Content — sits on the page background */}
        <AdasBox className='flex-1 p-6'>{activeItem?.content ?? <PlaceholderPanel title={`${activeItem?.label} settings`} />}</AdasBox>
      </AdasBox>
    </AdasBox>
  )
}
